import * as React from "react"

/** How long a finger rests on a message before its menu opens. */
const LONG_PRESS_MS = 450
/** Movement past this is a scroll, not a press. */
const MOVE_TOLERANCE_PX = 10
/** Two taps closer than this are a double tap. */
const DOUBLE_TAP_MS = 300

/** The native controls of a video own every gesture on them. */
function isVideo(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest("video") !== null
}

/** A tap on a control is that control's, never half of a double tap. */
function isControl(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest("button, a, input, video, [role=button]") !== null
  )
}

/**
 * The gestures that open a message's actions, wherever on the message they
 * happen: a long press with a finger or pen, a right click with a mouse
 * (and Android's own long-press `contextmenu`, which arrives as well and is
 * folded into the same open), and a double tap that reacts at once.
 *
 * The click that ends a long press is swallowed wherever it lands. By then
 * the menu is open and its backdrop is under the finger, so that click would
 * otherwise close the menu it just opened (or, on a picture, open the
 * lightbox as well).
 */
export function useMessagePress({
  onOpen,
  onDoubleTap,
}: {
  onOpen: () => void
  onDoubleTap?: () => void
}) {
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const origin = React.useRef<{ x: number; y: number } | null>(null)
  const fired = React.useRef(false)
  const lastTap = React.useRef(0)

  const cancel = React.useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
    origin.current = null
  }, [])

  React.useEffect(() => cancel, [cancel])

  /** Eats the next click anywhere, for a moment after a long press. */
  const swallowNextClick = React.useCallback(() => {
    const swallow = (event: MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()
      done()
    }
    const done = () => {
      window.removeEventListener("click", swallow, true)
      clearTimeout(expiry)
    }
    // Not every browser sends a click after a long press; never eat a later,
    // deliberate one.
    const expiry = setTimeout(done, 1000)
    window.addEventListener("click", swallow, true)
  }, [])

  return {
    onPointerDown(event: React.PointerEvent) {
      if (event.pointerType === "mouse" || isVideo(event.target)) return
      fired.current = false
      origin.current = { x: event.clientX, y: event.clientY }
      timer.current = setTimeout(() => {
        timer.current = null
        fired.current = true
        // Long enough to absorb Android's own `contextmenu` for this press,
        // short enough that a later right click still opens the menu.
        setTimeout(() => (fired.current = false), 1000)
        navigator.vibrate?.(10)
        swallowNextClick()
        onOpen()
      }, LONG_PRESS_MS)
    },

    onPointerMove(event: React.PointerEvent) {
      const start = origin.current
      if (!start) return
      if (
        Math.hypot(event.clientX - start.x, event.clientY - start.y) >
        MOVE_TOLERANCE_PX
      ) {
        cancel()
      }
    },

    onPointerUp(event: React.PointerEvent) {
      if (event.pointerType === "mouse") return
      const wasTap = origin.current !== null && !fired.current
      cancel()
      if (!wasTap || !onDoubleTap || isControl(event.target)) return

      const now = event.timeStamp
      if (now - lastTap.current < DOUBLE_TAP_MS) {
        lastTap.current = 0
        onDoubleTap()
      } else {
        lastTap.current = now
      }
    },

    onPointerCancel: cancel,

    onContextMenu(event: React.MouseEvent) {
      if (isVideo(event.target)) return
      event.preventDefault()
      // On Android the long press has usually opened the menu already.
      if (!fired.current) onOpen()
    },
  }
}
